import axiosInstance from "./axiosInstance";
import logger from "../utils/logger";
import { extractErrorMessage } from "../utils/errorHandler";

/**
 * Admin mapping from a Meta Click-to-WhatsApp Ad signal (clid > adId >
 * campaignId, in specificity order) to one of our recruiter-owned
 * referral links. See the backend MetaAdAttributionEntity for the
 * full data model.
 */
export interface MetaAdAttribution {
    id: string;
    ctwaClid?: string | null;
    adId?: string | null;
    campaignId?: string | null;
    referralLinkId?: string | null;
    referralLinkTitle?: string | null;
    referralLinkCode?: string | null;
    notes?: string | null;
    createdBy?: number | null;
    creatorName?: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface MetaAdAttributionWithBackfill extends MetaAdAttribution {
    /** Sessions retroactively attributed by this create/update. */
    backfilledSessionCount: number;
}

export interface UnmappedCtwaSummary {
    ctwaClid: string;
    adId?: string | null;
    campaignId?: string | null;
    sessionCount: number;
    firstSeenAt: string;
    lastSeenAt: string;
}

export interface CreateMetaAdAttributionRequest {
    ctwaClid?: string | null;
    adId?: string | null;
    campaignId?: string | null;
    referralLinkId?: string | null;
    notes?: string | null;
}

export interface UpdateMetaAdAttributionRequest {
    id: string;
    referralLinkId?: string | null;
    notes?: string | null;
}

export const fetchMetaAdAttributionsRequest = async (): Promise<MetaAdAttribution[]> => {
    try {
        logger.info('Fetching Meta ad attributions');
        const response = await axiosInstance.get<MetaAdAttribution[]>('/api/meta-ad-attributions');
        return response.data;
    } catch (error: unknown) {
        logger.error('Error fetching Meta ad attributions:', extractErrorMessage(error));
        throw error;
    }
};

export const fetchUnmappedCtwaRequest = async (): Promise<UnmappedCtwaSummary[]> => {
    try {
        logger.info('Fetching unmapped CTWA summaries');
        const response = await axiosInstance.get<UnmappedCtwaSummary[]>('/api/meta-ad-attributions/unmapped');
        return response.data;
    } catch (error: unknown) {
        logger.error('Error fetching unmapped CTWA:', extractErrorMessage(error));
        throw error;
    }
};

export const fetchMetaAdAttributionByIdRequest = async (id: string): Promise<MetaAdAttribution> => {
    try {
        const response = await axiosInstance.get<MetaAdAttribution>(`/api/meta-ad-attributions/${id}`);
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error fetching Meta ad attribution ${id}:`, extractErrorMessage(error));
        throw error;
    }
};

export const createMetaAdAttributionRequest = async (
    data: CreateMetaAdAttributionRequest,
): Promise<MetaAdAttributionWithBackfill> => {
    try {
        logger.info('Creating Meta ad attribution');
        const response = await axiosInstance.post<MetaAdAttributionWithBackfill>(
            '/api/meta-ad-attributions',
            data,
        );
        return response.data;
    } catch (error: unknown) {
        logger.error('Error creating Meta ad attribution:', extractErrorMessage(error));
        throw error;
    }
};

export const updateMetaAdAttributionRequest = async ({
    id,
    ...data
}: UpdateMetaAdAttributionRequest): Promise<MetaAdAttributionWithBackfill> => {
    try {
        logger.info('Updating Meta ad attribution');
        const response = await axiosInstance.put<MetaAdAttributionWithBackfill>(
            `/api/meta-ad-attributions/${id}`,
            data,
        );
        return response.data;
    } catch (error: unknown) {
        logger.error(`Error updating Meta ad attribution ${id}:`, extractErrorMessage(error));
        throw error;
    }
};

export const deleteMetaAdAttributionRequest = async (id: string): Promise<void> => {
    try {
        logger.info(`Deleting Meta ad attribution ${id}`);
        await axiosInstance.delete(`/api/meta-ad-attributions/${id}`);
    } catch (error: unknown) {
        logger.error(`Error deleting Meta ad attribution ${id}:`, extractErrorMessage(error));
        throw error;
    }
};
